interface StepResult {
  cacheKey: string;
  projectId: string;
  stepId: string;
  data: any;
  timestamp: number;
}

class IndexedDB {
  private dbName = 'scrana-step-results';
  private version = 2;
  private storeName = 'projectStepResults';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'cacheKey' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('projectId', 'projectId', { unique: false });
        }
      };
    });
  }

  async saveStepResult(projectId: string, stepId: string, data: any): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const stepResult: StepResult = {
        cacheKey: `${projectId}:${stepId}`,
        projectId,
        stepId,
        data,
        timestamp: Date.now(),
      };

      const request = store.put(stepResult);
      
      request.onerror = () => {
        console.error('IndexedDB save error:', request.error);
        reject(request.error);
      };
      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async getStepResult(projectId: string, stepId: string): Promise<any | null> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(`${projectId}:${stepId}`);

      request.onerror = () => {
        console.error('IndexedDB get error:', request.error);
        reject(request.error);
      };
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
    });
  }

  async getAllStepResultKeys(projectId: string): Promise<string[]> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.index('projectId').getAll(projectId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve((request.result as StepResult[]).map((result) => result.stepId));
      };
    });
  }

  async deleteStepResult(projectId: string, stepId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(`${projectId}:${stepId}`);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clearProjectStepResults(projectId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.index('projectId').openKeyCursor(IDBKeyRange.only(projectId));
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getStepResults(projectId: string, stepIds: string[]): Promise<Record<string, any>> {
    if (!this.db) {
      await this.init();
    }

    const results: Record<string, any> = {};
    
    // Get all results in parallel
    const promises = stepIds.map(async (stepId) => {
      try {
        const data = await this.getStepResult(projectId, stepId);
        if (data !== null) {
          results[stepId] = data;
        }
      } catch (error) {
        console.warn(`Failed to load step result for ${stepId}:`, error);
      }
    });

    await Promise.all(promises);
    return results;
  }
}

export const indexedDB = new IndexedDB();
